import {
  LIMIT_FIELDS,
  LimitDomains,
  LimitField,
  LimitMetricSpec,
  LimitResult,
  LimitValues,
  admissibleDomainEnds,
  deriveDefaultLimits,
  resolveEffectiveThermostatLimitDomains,
  validateLimitValues,
} from '../../../../../../common/thermostat-limits';
import { convertLimitTemperature } from '../../../util/convert-temp-units';
import { MyThermostat } from '../types';

export function resolveDeviceLimitDomains(
  device?: MyThermostat,
): LimitResult<LimitDomains> {
  return resolveEffectiveThermostatLimitDomains({
    source: { type: device?.source?.type ?? undefined },
    deviceId: device?.deviceId ?? undefined,
    meta: {
      manufacturer: device?.meta?.manufacturer ?? undefined,
      type: device?.meta?.type ?? undefined,
    },
    deviceSpec: {
      thermostat: {
        controlContract:
          device?.deviceSpec?.thermostat?.controlContract ?? undefined,
      },
    },
    deviceMetrics: device?.deviceMetrics?.map((entry) => ({
      key: entry?.key,
      value: entry?.value
        ? {
            value: entry.value.value ?? undefined,
            units: entry.value.units ?? undefined,
            unit: entry.value.unit ?? undefined,
            valueType: entry.value.valueType ?? undefined,
            valueStep: (entry.value as LimitMetricSpec).valueStep ?? undefined,
            defaultValue: entry.value.defaultValue,
            readOnly: entry.value.readOnly ?? undefined,
          }
        : undefined,
    })),
  });
}
export function deviceDefaultLimits(
  device?: MyThermostat,
): Partial<LimitValues> {
  const domains = resolveDeviceLimitDomains(device);
  if (!domains.ok) return {};
  const defaults = deriveDefaultLimits(domains.value);
  return defaults.ok ? defaults.value : {};
}
export function displayLimitBounds(
  device: MyThermostat | undefined,
  field: LimitField,
  units: 'C' | 'F',
) {
  const domains = resolveDeviceLimitDomains(device);
  if (!domains.ok) return undefined;
  const domain = domains.value[field];
  const ends = admissibleDomainEnds(domain);
  // Open continuous intervals have no selectable endpoint. Use an explicit
  // number field and shared validation instead of inventing epsilon bounds.
  if (ends.minimum === undefined || ends.maximum === undefined)
    return undefined;
  return {
    min: convertLimitTemperature(ends.minimum, units)!,
    max: convertLimitTemperature(ends.maximum, units)!,
    step:
      domain.step === undefined
        ? undefined
        : domain.step * (units === 'F' ? 1.8 : 1),
  };
}
export function validateDisplayLimits(
  values: Partial<Record<LimitField, number>>,
  device: MyThermostat | undefined,
  units: 'C' | 'F',
) {
  const domains = resolveDeviceLimitDomains(device);
  if (!domains.ok) return domains.issues;
  const canonical = Object.fromEntries(
    LIMIT_FIELDS.map((field) => [
      field,
      convertLimitTemperature(values[field], 'C', units),
    ]),
  ) as LimitValues;
  const issues = validateLimitValues(canonical, domains.value);
  if (
    ['Core', 'HMM'].includes(device?.source?.type ?? '') &&
    Number.isFinite(canonical.maxCoolSetpointLimit) &&
    Number.isFinite(canonical.minHeatSetpointLimit) &&
    canonical.maxCoolSetpointLimit - canonical.minHeatSetpointLimit < 2
  )
    return [
      ...issues,
      {
        code: 'CORE_CROSS_MODE_LIMITS',
        message: 'Cooling maximum must be at least 2 °C above heating minimum.',
      },
    ];
  return issues;
}
export function limitCapabilitySignature(device?: MyThermostat) {
  return JSON.stringify({
    source: device?.source?.type,
    domains: device?.deviceMetrics
      ?.filter((entry) => LIMIT_FIELDS.includes(entry?.key as LimitField))
      .map((entry) => [
        entry?.key,
        {
          value: entry?.value?.value,
          valueStep: (entry?.value as LimitMetricSpec)?.valueStep,
          defaultValue: entry?.value?.defaultValue,
          units: entry?.value?.units,
          unit: entry?.value?.unit,
          valueType: entry?.value?.valueType,
          readOnly: entry?.value?.readOnly,
        },
      ])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  });
}
export function canEditDeviceLimits(device?: MyThermostat) {
  if (!resolveDeviceLimitDomains(device).ok) return false;
  if (['Core', 'HMM'].includes(device?.source?.type ?? '')) return true;
  return (
    device?.source?.type === 'Conductor' &&
    device?.isMitsubishiThermostat === true &&
    LIMIT_FIELDS.every((field) =>
      device?.deviceMetrics?.some(
        (entry) => entry?.key === field && entry.value?.readOnly === false,
      ),
    )
  );
}
